import {AbstractFetcher} from '@/common/services/fetcher/AbstractFetcher';
import * as configRequests from './requests/configRequests';
import * as searchRequests from './requests/searchRequests';
import * as locationRequests from './requests/locationRequests';
import * as forecastRequests from './requests/forecastRequests';
import * as mapRequests from './requests/mapRequests';
import * as miscRequests from './requests/miscRequests';

function bindRequests<T extends {[k: string]: (providedFetcher: AbstractFetcher) => (...args: any[]) => any}>(
	fetcher: AbstractFetcher,
	methods: T,
): {[k in keyof T]: ReturnType<T[k]>} {
	return Object.entries(methods).reduce((map, [name, handler]) => {
		map[name] = handler(fetcher);
		return map;
	}, {} as any);
}

export class BackendAPI {
	config = bindRequests(this.fetcher, configRequests);
	search = bindRequests(this.fetcher, searchRequests);
	location = bindRequests(this.fetcher, locationRequests);
	forecast = bindRequests(this.fetcher, forecastRequests);
	map = bindRequests(this.fetcher, mapRequests);
	misc = bindRequests(this.fetcher, miscRequests);

	// eslint-disable-next-line @typescript-eslint/ban-types
	private readonly cache = new Map<Function, {params: string; result: any}[]>();
	private cacheCapacityPerRequest = 100;
	private cacheAllowed = true;

	constructor(private fetcher: AbstractFetcher) {
		fetcher.setDefaultParams({
			method: 'POST',

			headers: {
				// Default post data type
				'Content-Type': 'application/json',

				// Add specific static XHR header required by server
				'X-Requested-With': 'XMLHttpRequest',
			},
			// Rewrite an empty body with an empty JSON object
			postData: '{}',
		});
	}

	/** Disable caching to save memory */
	disableCache() {
		this.cacheAllowed = true;
	}

	/** Run a request and cache a response or return immediately a previously cached response if there is in cache */
	async cachedRequest<Args extends any[], Res extends any>(request: (...args: Args) => Promise<Res>, args: Args) {
		if (!this.cacheAllowed) {
			return request(...args);
		}

		let cacheSpace = this.cache.get(request);
		if (!cacheSpace) {
			cacheSpace = [];
			this.cache.set(request, cacheSpace);
		}

		// Used object comparison by comparing json string as fastest way.
		// However, it should be borne in mind that THE ORDER OF THE KEYS MATTERS.
		const params = JSON.stringify(args);
		const cachedResult = cacheSpace!.find(item => item.params === params);

		if (cachedResult) {
			return cachedResult.result as Res;
		}

		const result = await request(...args);

		cacheSpace.unshift({result, params});
		if (cacheSpace.length > this.cacheCapacityPerRequest) {
			cacheSpace.pop();
		}

		return result;
	}
}
